import { useCallback } from "react";
import { queryOptions, useQuery, useQueryClient } from "@tanstack/react-query";

import { ApiResponse, api, noop } from "_/utils";

const SCOPE = "docs";
const KEYS = {
  manifest: () => [SCOPE, "manifest"],
  index: () => [SCOPE, "index"],
  page: (slug?: string) => [SCOPE, "page", slug],
};

const ONE_HOUR = 1000 * 60 * 60;
const ONE_DAY = 24 * ONE_HOUR;

export type DocsIndexDoc = {
  slug: string;
  breadcrumbs: string[];
  title: string;
};

export type DocMeta = {
  private: boolean;
  draft: boolean;
  title: string;
  slug: string;
  path: string;
  modifiedAt: Date;
  missingTranslation?: boolean;
};

type SlugMap = Record<string, DocMeta>;

export type LayoutNode = DocMeta & {
  children: LayoutNode[];
};

// One variant along a content dimension, e.g. a single Hylo version.
export type DimensionVariant = {
  // Stable key referenced by `<Tab appliesTo>` and by the stored selection.
  value: string;
  label: string;
};

// The "how do I identify my value?" link shown at the right of the tab bar.
// `href` is a docs-relative path (e.g. `/hylo/machine_identification`).
export type ContentDimensionHelp = {
  href: string;
  label: string;
};

// A declared content dimension served in the manifest. `values` is ordered
// oldest to newest and is the full set of tabs rendered for the dimension.
// Every field is required: a dimension central enough to declare is one whose
// readers need a way to work out which variant they have.
export type ContentDimension = {
  // The value selected when the reader has made no choice yet.
  default: string;
  values: DimensionVariant[];
  help: ContentDimensionHelp;
};

// Content dimensions keyed by the `kind` that `<Tabs>` names. Empty when the
// docs repo declares none.
export type ContentDimensions = Record<string, ContentDimension>;

export type Manifest = {
  slugs: SlugMap;
  version: string;
  layout: LayoutNode[];
  contentDimensions: ContentDimensions;
};

export function useDocsManifest() {
  return useQuery({
    queryKey: KEYS.manifest(),
    queryFn: async () => {
      const result = await api.get<ApiResponse<Manifest>>("/docs/manifest");
      return result.data.data;
    },
    refetchOnWindowFocus: false,
    staleTime: ONE_HOUR,
  });
}

export function useDocPage(slug: string = "index") {
  return useQuery(docPageQuery(slug));
}

export function useDocsSearchIndex() {
  return useQuery({
    queryKey: KEYS.index(),
    queryFn: async () => {
      const result = await api.get<Record<string, DocsIndexDoc>>("/docs/index");
      return result.data;
    },
    refetchOnWindowFocus: false,
    staleTime: ONE_DAY,
  });
}

async function getDocPage(
  slug: string
): Promise<{ content: string; meta: DocMeta }> {
  const uri = `/docs/content/${slug}`;
  const result =
    await api.get<ApiResponse<{ content: string; meta: DocMeta }>>(uri);
  return result.data.data;
}

function docPageQuery(slug: string = "index") {
  return queryOptions({
    queryKey: KEYS.page(slug),
    queryFn: () => getDocPage(slug),
    refetchOnWindowFocus: false,
    staleTime: ONE_HOUR,
  });
}

export function usePrefetchDoc() {
  const queryClient = useQueryClient();
  return useCallback(
    (slug: string = "index") =>
      queryClient.query(docPageQuery(slug)).catch(noop),
    [queryClient]
  );
}

export function docAssetPath(src: string) {
  return `/api/v0/docs/assets/${src}`;
}
